import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';

import Heading from './Heading';
import HandOffLink from '../../../utils/urls/HandOffLink';

import colors from '../../../styling/colors';
import {
  MOBILE_END_QUERY,
  DESKTOP_START_QUERY,
  DESKTOP_MEDIUM_START_QUERY,
} from '../../../styling/mixins/responsive';
import typography from '../../../styling/mixins/typography';
import { PaddedLandingPageComponent } from '../../../styling/layout-containers/LandingPageContainers';

import unescape from '../../../utils/unescape';

import LazyImage from '../../Common/LazyImage';

const Container = styled(PaddedLandingPageComponent)`
  margin: 2rem 0;
  padding: 0 1rem;
`;

const PostWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin: 0 -0.5rem;

  @media (${DESKTOP_START_QUERY}) {
    flex-direction: row;
  }

  @media (${DESKTOP_MEDIUM_START_QUERY}) {
    margin: 0 -1rem 1rem;
  }

  /* IE 11 fix to remove flexbox at smaller sizes when not needed */
  @media all and (${MOBILE_END_QUERY}) and (-ms-high-contrast: active),
    all and (${MOBILE_END_QUERY}) and (-ms-high-contrast: none) {
    display: block;
  }
`;

const postStyles = css`
  align-items: center;
  color: ${colors('lapel')};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  text-decoration: none;
  width: 100%;
  margin-bottom: 1rem;

  @media (${DESKTOP_MEDIUM_START_QUERY}) {
    margin-bottom: 0px;
  }
`;

const PostDetails = styled.div`
  padding: 0 0.5rem;
  margin-bottom: 13px;
  width: 50%;

  @media (${DESKTOP_MEDIUM_START_QUERY}) {
    padding: 0 1rem;
  }
`;

const PostImage = styled.div`
  width: 50%;
  padding: 0 0.5rem;

  @media (${DESKTOP_MEDIUM_START_QUERY}) {
    padding: 0 1rem;
  }

  img {
    max-width: 100%;
  }
`;

const PostTitle = styled.h3`
  ${typography.headingXSmall};
  margin: 0 0 1rem 0;
  font-weight: 500;

  @media (${DESKTOP_START_QUERY}) {
    ${typography.headingSmall};
    ${typography.PlantinFont};
  }
`;

const Image = styled(LazyImage)`
  /* IE 11 fix to prevent the image from expanding the size of the page      */
  /* This doesn't fix extremely small browser sizes, but minimizes the issue */
  @media all and (-ms-high-contrast: active),
    all and (-ms-high-contrast: none) {
    max-height: 250px;
  }
`;

const PostReadMore = styled.div`
  ${typography.linkStyles};
`;

class BlogBlock extends Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
    href: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
  };

  render() {
    const { href, image } = this.props;
    const title = unescape(this.props.title);

    const Post = styled(HandOffLink)`
      ${postStyles};
    `;

    return (
      <Post href={href} identifier={title}>
        <PostImage>
          <Image src={image} alt={title} />
        </PostImage>
        <PostDetails>
          <PostTitle>{title}</PostTitle>
          <PostReadMore>Read on the blog</PostReadMore>
        </PostDetails>
      </Post>
    );
  }
}

export default class BlogBlocks extends Component {
  static propTypes = {
    className: PropTypes.string,
    posts: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        href: PropTypes.string.isRequired,
        image: PropTypes.string.isRequired,
      }),
    ),
    title: PropTypes.string.isRequired,
  };

  render() {
    const { className, posts, title } = this.props;

    return (
      <Container className={className}>
        <Heading title={title} />
        <PostWrapper>
          {posts.map(post => (
            <BlogBlock key={post.id} {...post} />
          ))}
        </PostWrapper>
      </Container>
    );
  }
}
